import { useState } from 'react';
import type { Market } from '@ouradent/shared';
import { createLead, queryClient } from '../../lib/data';
import { supabase } from '../../lib/supabase';
import { CloseX, useDrawer, useToast } from '../../ui/kit';

const SOURCES = ['Cold call', 'Referral', 'Cold email', 'FB instant form', 'WhatsApp'];

type FromCall = { id: string; person?: string | undefined; email?: string | undefined };

export function NewLeadDrawer({ fromCall }: { fromCall?: FromCall | undefined } = {}) {
  const { open, close } = useDrawer();
  const toast = useToast();
  const [f, setF] = useState({
    person: fromCall?.person ?? '',
    clinic: '',
    city: '',
    market: 'TR' as Market,
    email: fromCall?.email ?? '',
    phone: '',
    source: 'Cold call',
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF({ ...f, [k]: e.target.value });

  async function submit() {
    if (!f.person.trim() || !f.clinic.trim()) {
      setErr('Add the person and the clinic.');
      return;
    }
    if (!f.email.trim() && !f.phone.trim()) {
      setErr('Add an email or a phone number, so we can reach them and spot duplicates.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const { id, existed } = await createLead(f);
      if (fromCall) {
        const { error } = await supabase.rpc('link_call', { p_call: fromCall.id, p_lead: id });
        if (error) throw new Error(error.message);
        await queryClient.invalidateQueries();
        toast(existed ? 'Linked to the existing lead.' : 'Lead added and the call linked.');
        open({ type: 'call', id: fromCall.id });
        return;
      }
      toast(existed ? 'This lead already exists. Opened it.' : 'Lead added. Set the next step.');
      open({ type: 'lead', id, edit: existed ? undefined : 'change' });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="dhead">
        <div>
          <h2 id="dtitle">{fromCall ? 'Add lead from call' : 'Add lead'}</h2>
          <div className="sm">
            {fromCall
              ? 'The call is linked to this lead as soon as you add it.'
              : 'For cold calls and referrals. Form leads arrive on their own.'}
          </div>
        </div>
        <CloseX />
      </div>
      <div className="dbody">
        <div className="panel grid2">
          <label className="field">
            <span>Person</span>
            <input
              type="text"
              placeholder="Dr. Jane Smith"
              value={f.person}
              onChange={set('person')}
            />
          </label>
          <label className="field">
            <span>Clinic</span>
            <input type="text" value={f.clinic} onChange={set('clinic')} />
          </label>
          <label className="field">
            <span>City</span>
            <input type="text" value={f.city} onChange={set('city')} />
          </label>
          <label className="field">
            <span>Market</span>
            <select aria-label="Market" value={f.market} onChange={set('market')}>
              <option>TR</option>
              <option>UK</option>
            </select>
          </label>
          <label className="field">
            <span>Email</span>
            <input type="email" value={f.email} onChange={set('email')} />
          </label>
          <label className="field">
            <span>Phone</span>
            <input type="text" inputMode="tel" value={f.phone} onChange={set('phone')} />
          </label>
          <label className="field">
            <span>Source</span>
            <select aria-label="Source" value={f.source} onChange={set('source')}>
              {SOURCES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
        </div>
        {err && (
          <p style={{ color: 'var(--red)' }} role="alert">
            {err}
          </p>
        )}
      </div>
      <div className="dfoot">
        <button className="btn primary" onClick={() => void submit()} disabled={busy}>
          {busy ? 'Adding…' : 'Add lead'}
        </button>
        <button className="btn ghost" onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
